import React, { useEffect, useRef, useState, MutableRefObject } from "react";
import { useStore } from "../../store/store";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

interface RefObj extends HTMLDivElement {
  current: unknown;
}

function useCarouselSelection(reset: () => void, bifold: string) {
  const theme = useTheme();
  const media = useMediaQuery(theme.breakpoints.up("md"));
  const mediaXL = useMediaQuery(theme.breakpoints.up("xl"));

  const screenCutoff1 = useMediaQuery("(min-width:1655px)", { noSsr: true });
  const screenCutoff2 = useMediaQuery("(min-width:1400px)", { noSsr: true });

  const largeScreen =
    !screenCutoff1 && screenCutoff2 ? false : media && mediaXL ? true : false;
  const mediumScreen =
    (!screenCutoff1 && screenCutoff2) || (media && !mediaXL) ? true : false;

  const addToQuote = useStore((state) => state.addToQuote);

  const [hover, setHover] = useState("");

  const [startX, setStartX] = useState<number | null>(null);

  const [numPages, setNumPages] = useState(2);

  const handleHover = (unit: string) => setHover(unit);

  const handleClick = (tab: string) => {
    reset();

    switch (tab) {
      case "bifold":
        const doorType = bifold?.replace("F", "f")
        addToQuote({ doorType });
        break;
      case "slider":
        addToQuote({ doorType: "Slider" });
        break;
      case "swing":
        addToQuote({ doorType: "Swing" });
        break;
      case "fixed":
        addToQuote({ doorType: "Fixed Panel" });
        break;
      case "pivot":
        addToQuote({ doorType: "Pivot" });
        break;
      default:
        return;
    }
  };

  const boxRef: MutableRefObject<RefObj | null> = useRef(null);
  const carouselRef: MutableRefObject<RefObj | null> = useRef(null);

  const [index, setIndex] = useState(1);

  useEffect(() => {
    const initialIndex = largeScreen ? 1 : mediumScreen && !largeScreen ? 0 : 1;

    setIndex(initialIndex);
  }, [media, mediaXL, screenCutoff1, screenCutoff2]);

  useEffect(() => {
    if (!boxRef?.current) return;

    if (!carouselRef?.current) return;

    const boxWidth: unknown =
      "offsetWidth" in boxRef.current ? boxRef.current.offsetWidth : 0;
    const translateX =
      typeof boxWidth === "number" ? -(index - 1) * boxWidth : 0;
    const mult = largeScreen ? 1.6 : mediumScreen ? 1.4 : 1;

    if ("style" in carouselRef.current) {
      carouselRef.current.style.width =
        typeof boxWidth === "number" ? `${boxWidth * 5}px` : "";
      carouselRef.current.style.transform = `translateX(${
        translateX * mult
      }px)`;
    }

    const pageNum = largeScreen ? 2 : mediumScreen ? 4 : 5;
    setNumPages(pageNum);
  }, [index, media, mediaXL, screenCutoff1, screenCutoff2]);

  const shiftLeft = () => {
    if (largeScreen && index === 1) {
      return setIndex(2);
    }

    if (!largeScreen && mediumScreen && index === 0) {
      return setIndex(3);
    }

    if (!largeScreen && !mediumScreen && index === 1) {
      return setIndex(5);
    }

    const newIndex = (index - 1 + 5) % 5;
    setIndex(newIndex);
  };

  const shiftRight = () => {
    if (largeScreen && index === 2) {
      return setIndex(1);
    }

    if (!largeScreen && mediumScreen && index === 3) {
      return setIndex(0);
    }

    if (!largeScreen && !mediumScreen) {
      if (index === 4) {
        return setIndex(5);
      }
      if (index === 5) {
        return setIndex(1);
      }
    }

    const newIndex = (index + 1) % 5;
    setIndex(newIndex);
  };

  const pageClick = (dotNum: number) => {
    setIndex(dotNum);
  };

  const handleTouchStart = (event: React.TouchEvent<unknown>) => {
    setStartX(event.touches[0].clientX);
  };

  const handleTouchMove = (event: React.TouchEvent<unknown>) => {
    if (startX === null) {
      return;
    }

    const diffX = event.touches[0].clientX - startX;
    const boxWidth =
      boxRef?.current && "offsetWidth" in boxRef.current
        ? boxRef.current.offsetWidth
        : 0;
    const moveRatio = typeof boxWidth === "number" ? diffX / boxWidth : 0;

    if (moveRatio > 0.2) {
      shiftLeft();
      setStartX(null);
    } else if (moveRatio < -0.2) {
      shiftRight();
      setStartX(null);
    }
  };

  const handleTouchEnd = () => {
    setStartX(null);
  };

  return {
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
    pageClick,
    shiftRight,
    shiftLeft,
    hover,
    numPages,
    handleHover,
    handleClick,
    boxRef,
    carouselRef,
    largeScreen,
    mediumScreen,
    index,
  };
}

export default useCarouselSelection;
